import React, { useState, useEffect, useMemo } from 'react'
import { api } from '../api'
import ExportButton from '../components/ExportButton'
import LastUpdated from '../components/LastUpdated'

const STATUSES = ['Done', 'Dev', 'RFC', 'Beta Flight', 'External Flight', 'Not started', 'Cut', 'TBD', 'GA']
const PRIORITIES = ['P0', 'P1', 'P2', 'TBD']
const GROUP_OPTIONS = ['status', 'mission', 'owner', 'priority', 'squad']
const FLIGHT_WEEKS = ['', 'W1', 'W2', 'W3', 'W4', 'W5', 'W6', 'W7', 'W8']
const PRIORITY_ORDER = { 'P0': 0, 'P1': 1, 'P2': 2, 'TBD': 3 }
const STATUS_ORDER = { 'Done': 0, 'Dev': 1, 'RFC': 2, 'Beta Flight': 3, 'External Flight': 4, 'GA': 5, 'Not started': 6, 'TBD': 7, 'Cut': 8 }

const statusBadgeColors = {
  'Done': { bg: '#2E7D32', text: '#fff' },
  'Dev': { bg: '#2196F3', text: '#fff' },
  'RFC': { bg: '#FF9800', text: '#fff' },
  'Beta Flight': { bg: '#8BC34A', text: '#fff' },
  'External Flight': { bg: '#4CAF50', text: '#fff' },
  'Not started': { bg: '#E0E0E0', text: '#666' },
  'Cut': { bg: '#F44336', text: '#fff' },
  'TBD': { bg: '#9E9E9E', text: '#fff' },
  'GA': { bg: '#00BCD4', text: '#fff' },
}

const groupHeaderColors = {
  status: '#E3F2FD',
  mission: '#F3E5F5',
  owner: '#E8F5E9',
  priority: '#FFF3E0',
  squad: '#F5F5F5',
}

const designBadgeColors = {
  'Design complete': { bg: '#E8F5E9', text: '#2E7D32' },
  'Need Design': { bg: '#FFF3E0', text: '#E65100' },
  'N/A': { bg: '#F5F5F5', text: '#999' },
}

function getFlightWeekColor(fw) {
  if (!fw) return null
  const num = parseInt(fw.replace('W', ''))
  if (num <= 2) return { bg: '#E8F5E9', text: '#2E7D32' }
  if (num <= 4) return { bg: '#FFF8E1', text: '#F57F17' }
  return { bg: '#FFEBEE', text: '#C62828' }
}

export default function WorkItems() {
  const [items, setItems] = useState([])
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({ squad: 'canvas', status: '', priority: '', mission: '', owner: '' })
  const [groupBy, setGroupBy] = useState('status')
  const [editingCell, setEditingCell] = useState(null)
  const [editValue, setEditValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [collapsedGroups, setCollapsedGroups] = useState({})
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' })

  useEffect(() => {
    Promise.all([api.getWorkItems(), api.getSquadMembers()])
      .then(([w, m]) => { setItems(w); setMembers(m) })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  const emailToName = useMemo(() => {
    const map = {}
    members.forEach(m => { if (m.email) map[m.email] = m.fullName || m.id })
    return map
  }, [members])

  const missions = useMemo(() => [...new Set(items.map(i => i.mission).filter(Boolean))].sort(), [items])
  const owners = useMemo(() => [...new Set(items.map(i => i.owner).filter(Boolean))].sort(), [items])
  const squads = useMemo(() => [...new Set(items.map(i => i.squad).filter(Boolean))].sort(), [items])

  const filtered = useMemo(() => {
    return items.filter(i => {
      if (filters.squad && i.squad !== filters.squad) return false
      if (filters.status && i.status !== filters.status) return false
      if (filters.priority && i.priority !== filters.priority) return false
      if (filters.mission && i.mission !== filters.mission) return false
      if (filters.owner && i.owner !== filters.owner) return false
      return true
    })
  }, [items, filters])

  const grouped = useMemo(() => {
    const groups = {}
    filtered.forEach(i => {
      const key = i[groupBy] || 'Other'
      if (!groups[key]) groups[key] = []
      groups[key].push(i)
    })
    // Sort groups by STATUS_ORDER when grouping by status
    if (groupBy === 'status') {
      const sorted = {}
      Object.keys(groups)
        .sort((a, b) => (STATUS_ORDER[a] ?? 99) - (STATUS_ORDER[b] ?? 99))
        .forEach(key => sorted[key] = groups[key])
      return sorted
    }
    return groups
  }, [filtered, groupBy])

  const toggleGroup = (group) => {
    setCollapsedGroups(prev => ({ ...prev, [group]: !prev[group] }))
  }

  // (moved to top-level)

  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        if (prev.direction === 'asc') return { key, direction: 'desc' }
        return { key: null, direction: 'asc' } // 3rd click clears
      }
      return { key, direction: 'asc' }
    })
  }

  const sortItems = (items) => {
    if (!sortConfig.key) return items
    const sorted = [...items].sort((a, b) => {
      const key = sortConfig.key
      let av = a[key] || '', bv = b[key] || ''
      if (key === 'priority') { av = PRIORITY_ORDER[av] ?? 99; bv = PRIORITY_ORDER[bv] ?? 99 }
      else if (key === 'status') { av = STATUS_ORDER[av] ?? 99; bv = STATUS_ORDER[bv] ?? 99 }
      else if (key === 'owner') { av = emailToName[av] || av; bv = emailToName[bv] || bv }
      else { av = String(av).toLowerCase(); bv = String(bv).toLowerCase() }
      if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1
      if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1
      return 0
    })
    return sorted
  }

  const SortHeader = ({ field, children }) => (
    <th onClick={() => handleSort(field)}>
      {children}
      {sortConfig.key === field && <span className="sort-arrow">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
    </th>
  )

  const startEdit = (id, field, currentValue) => {
    setEditingCell({ id, field })
    setEditValue(currentValue || '')
  }

  const saveEdit = async () => {
    if (!editingCell) return
    setSaving(true)
    try {
      const { id, field } = editingCell
      const val = field === 'hasRisk' ? editValue === 'true' : editValue
      await api.updateWorkItem(id, { [field]: val })
      setItems(prev => prev.map(i => i.id === id ? { ...i, [field]: val } : i))
    } catch (e) { console.error(e) }
    setEditingCell(null)
    setSaving(false)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') saveEdit()
    if (e.key === 'Escape') setEditingCell(null)
  }

  const renderCell = (item, field) => {
    const isEditing = editingCell?.id === item.id && editingCell?.field === field
    const value = item[field] || ''

    if (isEditing) {
      if (field === 'status') {
        return <select autoFocus value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={saveEdit} className="edit-select">
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      }
      if (field === 'priority') {
        return <select autoFocus value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={saveEdit} className="edit-select">
          {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      }
      if (field === 'flightWeek') {
        return <select autoFocus value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={saveEdit} className="edit-select">
          {FLIGHT_WEEKS.map(w => <option key={w} value={w}>{w || '—'}</option>)}
        </select>
      }
      if (field === 'hasRisk') {
        return <select autoFocus value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={saveEdit} className="edit-select">
          <option value="false">No</option>
          <option value="true">⚠️ Yes</option>
        </select>
      }
      return <input autoFocus type="text" value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={saveEdit} onKeyDown={handleKeyDown} className="edit-input" />
    }

    // Status badge
    if (field === 'status') {
      const colors = statusBadgeColors[value] || { bg: '#999', text: '#fff' }
      return <span className="editable-cell" onClick={() => startEdit(item.id, field, value)}>
        <span className="status-badge" style={{ background: colors.bg, color: colors.text }}>{value || '—'}</span>
      </span>
    }

    // Priority badge
    if (field === 'priority') {
      return <span className="editable-cell" onClick={() => startEdit(item.id, field, value)}>
        <span className={`badge priority-${value?.toLowerCase()}`}>{value || '—'}</span>
      </span>
    }

    // Flight week badge
    if (field === 'flightWeek') {
      const colors = getFlightWeekColor(value)
      return <span className="editable-cell" onClick={() => startEdit(item.id, field, value)}>
        {colors ? <span className="flight-week-badge" style={{ background: colors.bg, color: colors.text }}>{value}</span> : '—'}
      </span>
    }

    // Design badge
    if (field === 'design') {
      const colors = designBadgeColors[value] || { bg: '#F5F5F5', text: '#666' }
      return <span className="editable-cell" onClick={() => startEdit(item.id, field, value)}>
        <span className="design-badge" style={{ background: colors.bg, color: colors.text }}>{value || '—'}</span>
      </span>
    }

    // Risk flag
    if (field === 'hasRisk') {
      return <span className="editable-cell" onClick={() => startEdit(item.id, field, String(item.hasRisk || false))}>
        {item.hasRisk ? <span className="risk-badge">⚠️</span> : '—'}
      </span>
    }

    // Owner - show fullName
    if (field === 'owner') {
      const name = emailToName[value] || (value ? value.split('@')[0] : '—')
      return <span className="editable-cell" onClick={() => startEdit(item.id, field, value)} title={value}>
        {name}
      </span>
    }

    return <span className="editable-cell" onClick={() => startEdit(item.id, field, value)}>
      {value || '—'}
    </span>
  }

  if (loading) return <div className="loading">Loading work items...</div>

  return (
    <div className="work-items-page">
      <div className="page-header">
        <h2>Work Items</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <LastUpdated data={items} />
          <span className="item-count">{filtered.length} items</span>
          <ExportButton data={filtered} filename="work-items" />
        </div>
      </div>

      <div className="filters-bar">
        <select value={filters.squad} onChange={e => setFilters(f => ({ ...f, squad: e.target.value }))}>
          <option value="">All Squads</option>
          {squads.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.priority} onChange={e => setFilters(f => ({ ...f, priority: e.target.value }))}>
          <option value="">All Priorities</option>
          {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={filters.mission} onChange={e => setFilters(f => ({ ...f, mission: e.target.value }))}>
          <option value="">All Missions</option>
          {missions.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={filters.owner} onChange={e => setFilters(f => ({ ...f, owner: e.target.value }))}>
          <option value="">All Owners</option>
          {owners.map(o => <option key={o} value={o}>{emailToName[o] || o.split('@')[0]}</option>)}
        </select>
        <div className="group-by">
          <label>Group by:</label>
          <select value={groupBy} onChange={e => setGroupBy(e.target.value)}>
            {GROUP_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
      </div>

      <div className="work-items-table-container">
        {Object.entries(grouped).map(([group, groupItems]) => (
          <div key={group} className="group-section">
            <div className="group-header" style={{ background: groupHeaderColors[groupBy] || '#F5F5F5' }} onClick={() => toggleGroup(group)}>
              <span className="group-collapse-icon">{collapsedGroups[group] ? '▶' : '▼'}</span>
              <span className="group-name">{groupBy === 'owner' ? (emailToName[group] || group) : group}</span>
              <span className="group-count" style={groupBy === 'status' && group === 'Cut' ? { background: '#9E9E9E' } : {}}>{groupItems.length}</span>
            </div>
            {!collapsedGroups[group] && (
              <table className="work-items-table">
                <thead>
                  <tr>
                    <SortHeader field="title">Title</SortHeader>
                    <SortHeader field="priority">Priority</SortHeader>
                    <SortHeader field="status">Status</SortHeader>
                    <SortHeader field="flightWeek">Flight Week</SortHeader>
                    <SortHeader field="owner">Owner</SortHeader>
                    <SortHeader field="mission">Mission</SortHeader>
                    <SortHeader field="design">Design</SortHeader>

                    <th>Status Update</th>
                  </tr>
                </thead>
                <tbody>
                  {sortItems(groupItems).map(item => (
                    <tr key={item.id}>
                      <td className="title-cell cell-wrap">
                        {item.workItemId && <span style={{ display: 'inline-block', fontSize: '0.7em', color: '#888', background: '#f0f0f0', borderRadius: 3, padding: '1px 5px', marginRight: 6, fontFamily: 'monospace', verticalAlign: 'middle' }}>{item.workItemId}</span>}
                        {renderCell(item, 'title')}
                      </td>
                      <td>{renderCell(item, 'priority')}</td>
                      <td>{renderCell(item, 'status')}</td>
                      <td>{renderCell(item, 'flightWeek')}</td>
                      <td className="owner-cell">{renderCell(item, 'owner')}</td>
                      <td>{renderCell(item, 'mission')}</td>
                      <td>{renderCell(item, 'design')}</td>
                      <td className="update-cell cell-wrap">{renderCell(item, 'statusUpdate')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
